import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Clock, User } from 'lucide-react';

export const BLOG_POSTS = [
  {
    slug: 'reduce-employee-no-shows',
    title: 'How To Reduce Employee No-Shows (Complete Guide)',
    metaTitle: 'How To Reduce Employee No-Shows | Strategies + Examples | ReStaff',
    metaDescription: 'Learn how to reduce employee no-shows with proven strategies, real examples, and workforce management solutions.',
    excerpt: 'Employee no-shows disrupt operations, increase labor costs, and create scheduling gaps. Preventing them requires clear policies, communication, and better scheduling systems.',
    image: 'https://images.pexels.com/photos/1181396/pexels-photo-1181396.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940',
    imageAlt: 'Business team meeting in modern conference room showing workplace collaboration',
    author: 'ReStaff Team',
    readTime: '10 min read',
    category: 'Workforce Management',
    ogImage: 'https://images.pexels.com/photos/1181396/pexels-photo-1181396.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940',
    canonical: 'https://getrestaff.co/blog/reduce-employee-no-shows',
  },
  {
    slug: 'how-to-stop-buddy-punching',
    title: 'How To Stop Buddy Punching Without Killing Team Trust',
    metaTitle: 'How To Stop Buddy Punching | Methods + Examples | ReStaff',
    metaDescription: 'Learn how to prevent buddy punching, reduce time theft, and improve time tracking accuracy without damaging team trust.',
    excerpt: 'Buddy punching occurs when one employee clocks in or out for another. This leads to inaccurate time tracking, payroll errors, and increased labor costs.',
    image: 'https://images.pexels.com/photos/7522609/pexels-photo-7522609.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940',
    imageAlt: 'Hand using fingerprint biometric security for digital time clock access',
    author: 'ReStaff Team',
    readTime: '10 min read',
    category: 'Workforce Management',
    ogImage: 'https://images.pexels.com/photos/7522609/pexels-photo-7522609.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940',
    canonical: 'https://getrestaff.co/blog/how-to-stop-buddy-punching',
  },
  {
    slug: 'employee-scheduling-methods',
    title: 'Employee Scheduling Methods That Actually Work',
    metaTitle: 'Employee Scheduling Methods | Best Strategies Explained | ReStaff',
    metaDescription: 'Discover the most effective employee scheduling methods with examples, pros and cons, and how to choose the right one.',
    excerpt: 'Employee scheduling is not one-size-fits-all. Different businesses require different scheduling methods depending on demand, staff size, and operations.',
    image: 'https://images.pexels.com/photos/6170652/pexels-photo-6170652.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940',
    imageAlt: 'Manager planning employee work schedule on desk calendar',
    author: 'ReStaff Team',
    readTime: '11 min read',
    category: 'Workforce Management',
    ogImage: 'https://images.pexels.com/photos/6170652/pexels-photo-6170652.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940',
    canonical: 'https://getrestaff.co/blog/employee-scheduling-methods',
  },
  {
    slug: 'time-tracking-vs-timesheets',
    title: 'Time Tracking vs Timesheets (Key Differences Explained)',
    metaTitle: 'Time Tracking vs Timesheets | Differences + Examples | ReStaff',
    metaDescription: 'Learn the difference between time tracking and timesheets, when to use each, and how they impact labor cost and payroll accuracy.',
    excerpt: 'Time tracking and timesheets are often used interchangeably, but they are not the same. Understanding the difference helps businesses improve accuracy, reduce errors, and manage labor costs more effectively.',
    image: 'https://images.pexels.com/photos/3942322/pexels-photo-3942322.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940',
    imageAlt: 'Analog clock representing time tracking and timesheet management',
    author: 'ReStaff Team',
    readTime: '9 min read',
    category: 'Workforce Management',
    ogImage: 'https://images.pexels.com/photos/3942322/pexels-photo-3942322.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940',
    canonical: 'https://getrestaff.co/blog/time-tracking-vs-timesheets',
  },
  {
    slug: 'manage-staff-multiple-locations',
    title: 'How To Manage Staff Across Multiple Locations',
    metaTitle: 'Manage Staff Across Multiple Locations | Complete Guide | ReStaff',
    metaDescription: 'Learn how to manage employees across multiple locations with scheduling strategies, real examples, and workforce optimization methods.',
    excerpt: 'Managing employees across multiple locations adds complexity to scheduling, communication, and labor cost control. Without proper systems, inefficiencies quickly increase.',
    image: 'https://images.pexels.com/photos/7654431/pexels-photo-7654431.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940',
    imageAlt: 'Office team coordinating operations across multiple business locations',
    author: 'ReStaff Team',
    readTime: '11 min read',
    category: 'Workforce Management',
    ogImage: 'https://images.pexels.com/photos/7654431/pexels-photo-7654431.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940',
    canonical: 'https://getrestaff.co/blog/manage-staff-multiple-locations',
  },
  {
    slug: 'how-to-reduce-overtime',
    title: 'How To Reduce Overtime Without Understaffing',
    metaTitle: 'How To Reduce Overtime Without Understaffing | ReStaff Guide',
    metaDescription: 'Learn how to reduce overtime costs without understaffing using scheduling strategies, real examples, and workforce optimization methods.',
    excerpt: 'Overtime increases labor costs and reduces profitability. The challenge is reducing overtime without creating staffing shortages or impacting operations.',
    image: 'https://images.pexels.com/photos/6285146/pexels-photo-6285146.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940',
    imageAlt: 'Business team reviewing workforce schedules and planning',
    author: 'ReStaff Team',
    readTime: '10 min read',
    category: 'Workforce Management',
    ogImage: 'https://images.pexels.com/photos/6285146/pexels-photo-6285146.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940',
    canonical: 'https://getrestaff.co/blog/how-to-reduce-overtime',
  },
  {
    slug: 'how-to-calculate-labor-cost',
    title: 'How To Calculate Labor Cost (Step-by-Step Guide)',
    metaTitle: 'How To Calculate Labor Cost | Formula + Examples | ReStaff',
    metaDescription: 'Learn how to calculate labor cost with simple formulas, real examples, and step-by-step instructions.',
    excerpt: 'Labor cost is the total cost of paying employees, including wages, taxes, and benefits. Calculating it accurately is essential for controlling expenses and maintaining profitability.',
    image: 'https://images.pexels.com/photos/6962992/pexels-photo-6962992.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940',
    imageAlt: 'Business professional using calculator for labor cost budgeting',
    author: 'ReStaff Team',
    date: '2026-03-26',
    dateFormatted: 'March 26, 2026',
    readTime: '10 min read',
    category: 'Workforce Management',
    ogImage: 'https://images.pexels.com/photos/6962992/pexels-photo-6962992.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940',
    canonical: 'https://getrestaff.co/blog/how-to-calculate-labor-cost',
  },
  {
    slug: 'labor-cost-percentage',
    title: 'What Is Labor Cost Percentage and How To Calculate It',
    metaTitle: 'Labor Cost Percentage Explained | Formula + Examples | ReStaff',
    metaDescription: 'Learn what labor cost percentage is, how to calculate it, and how to optimize it with real examples and simple formulas.',
    excerpt: 'Labor cost percentage measures how much of a business\'s revenue is spent on employee wages, taxes, and benefits. It is one of the most important metrics for controlling profitability.',
    image: 'https://images.pexels.com/photos/590011/pexels-photo-590011.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940',
    imageAlt: 'Business professional analyzing labor cost charts and financial data',
    author: 'ReStaff Team',
    date: '2026-03-26',
    dateFormatted: 'March 26, 2026',
    readTime: '9 min read',
    category: 'Workforce Management',
    ogImage: 'https://images.pexels.com/photos/590011/pexels-photo-590011.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940',
    canonical: 'https://getrestaff.co/blog/labor-cost-percentage',
  },
  {
    slug: 'how-to-track-employee-work-hours',
    title: 'How To Track Employee Work Hours (Complete Guide)',
    metaTitle: 'How To Track Employee Work Hours | Methods + Examples | ReStaff',
    metaDescription: 'Learn how to track employee work hours accurately using proven methods, examples, and workforce management strategies.',
    excerpt: 'Employee time tracking is the process of recording when employees start and finish work. Accurate tracking helps businesses control labor costs, improve payroll accuracy, and manage workforce efficiency.',
    image: 'https://images.pexels.com/photos/5185175/pexels-photo-5185175.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940',
    imageAlt: 'Hands and clock representing employee time tracking concept',
    author: 'ReStaff Team',
    date: '2026-03-26',
    dateFormatted: 'March 26, 2026',
    readTime: '11 min read',
    category: 'Workforce Management',
    ogImage: 'https://images.pexels.com/photos/5185175/pexels-photo-5185175.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940',
    canonical: 'https://getrestaff.co/blog/how-to-track-employee-work-hours',
  },
  {
    slug: 'how-to-schedule-employees-efficiently',
    title: 'How To Schedule Employees Efficiently (Step-by-Step Guide)',
    metaTitle: 'How To Schedule Employees Efficiently | Best Methods | ReStaff',
    metaDescription: 'Learn how to schedule employees efficiently with proven methods, examples, and workforce optimization strategies.',
    excerpt: 'Employee scheduling is the process of assigning shifts, managing availability, and ensuring the right number of staff are working at the right time. Learn the proven methods for efficient scheduling.',
    image: 'https://images.pexels.com/photos/6172482/pexels-photo-6172482.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940',
    imageAlt: 'Manager planning employee work schedule on desk calendar',
    author: 'ReStaff Team',
    date: '2026-03-26',
    dateFormatted: 'March 26, 2026',
    readTime: '10 min read',
    category: 'Workforce Management',
    ogImage: 'https://images.pexels.com/photos/6172482/pexels-photo-6172482.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940',
    canonical: 'https://getrestaff.co/blog/how-to-schedule-employees-efficiently',
  },
  {
    slug: 'how-to-reduce-labor-cost',
    title: 'How To Reduce Labor Cost (Complete Guide)',
    metaTitle: 'How To Reduce Labor Cost | Strategies + Examples | ReStaff',
    metaDescription: 'Learn how to reduce labor costs with proven strategies, real examples, and workforce optimization methods.',
    excerpt: 'Labor cost is one of the largest expenses for most businesses. Learn practical strategies to reduce labor costs through better scheduling, overtime control, and workforce optimization.',
    image: 'https://images.pexels.com/photos/7109290/pexels-photo-7109290.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940',
    imageAlt: 'Business team reviewing labor cost reports and workforce planning data',
    author: 'ReStaff Team',
    date: '2026-03-26',
    dateFormatted: 'March 26, 2026',
    readTime: '11 min read',
    category: 'Workforce Management',
    ogImage: 'https://images.pexels.com/photos/7109290/pexels-photo-7109290.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940',
    canonical: 'https://getrestaff.co/blog/how-to-reduce-labor-cost',
  },
  {
    slug: 'eva-ai-workforce-management',
    title: 'What Eva AI Can Do for Tenants Inside ReStaff (Complete Guide)',
    metaTitle: 'Eva AI for Workforce Management | ReStaff AI Assistant Guide',
    metaDescription: 'Discover how Eva AI helps tenants in ReStaff optimize schedules, reduce labor costs, and make smarter workforce decisions using real-time insights.',
    excerpt: 'Eva AI is the AI assistant built into ReStaff. It analyzes your schedules, timesheets, and labor data to surface insights, detect risks, and answer questions about your workforce — in plain language.',
    image: 'https://restaff.b-cdn.net/Eva/eva1.png',
    imageAlt: 'Eva AI assistant inside ReStaff workforce management platform',
    author: 'ReStaff Team',
    date: '2026-03-26',
    dateFormatted: 'March 26, 2026',
    readTime: '10 min read',
    category: 'AI & Insights',
    ogImage: 'https://restaff.b-cdn.net/Eva/eva1.png',
    canonical: 'https://getrestaff.co/blog/eva-ai-workforce-management',
  },
  {
    slug: 'manage-your-workforce-cut-labor-costs',
    title: 'Manage Your Workforce: Cut Labor Costs With Better Scheduling',
    metaTitle: 'Manage Your Workforce: Cut Labor Costs With Better Scheduling | ReStaff Blog',
    metaDescription: 'Learn how to manage your workforce and cut labor costs with smarter scheduling. Practical steps, templates, and software insights for multi-location teams.',
    excerpt: 'Labor costs are the single largest controllable expense for most businesses. Learn how smarter scheduling, real-time tracking, and the right tools can cut unnecessary spend without sacrificing service quality.',
    image: '/blog/manage-workforce-scheduling-software.jpg',
    imageAlt: 'ReStaff workforce scheduling software dashboard view',
    author: 'ReStaff Team',
    date: '2026-02-28',
    dateFormatted: 'February 28, 2026',
    readTime: '12 min read',
    category: 'Workforce Management',
    ogImage: 'https://getrestaff.co/blog/manage-workforce-scheduling-software.jpg',
    canonical: 'https://getrestaff.co/blog/manage-your-workforce-cut-labor-costs',
  },
  {
    slug: 'resend-smtp-staff-notifications',
    title: 'Resend Integration for Staff Notifications (Complete Guide)',
    metaTitle: 'Resend for Staff Notifications | Setup + Benefits | ReStaff',
    metaDescription: 'Learn how to use Resend in ReStaff to send shift notifications, announcements, and payslips automatically.',
    excerpt: 'ReStaff now allows businesses to send automated staff emails using Resend. This feature enables reliable delivery of shift assignments, announcements, and payslips directly to employees.',
    image: 'https://restaff.b-cdn.net/Screenshots/resend.png',
    imageAlt: 'Resend email notification configuration dashboard for staff communication',
    author: 'ReStaff Team',
    date: '2026-04-16',
    dateFormatted: 'April 16, 2026',
    readTime: '12 min read',
    category: 'Workforce Management',
    ogImage: 'https://restaff.b-cdn.net/Screenshots/resend.png',
    canonical: 'https://getrestaff.co/blog/resend-smtp-staff-notifications',
  },
];

export default function Blog() {
  useEffect(() => {
    document.title = 'Blog | ReStaff – Workforce Management Insights';
  }, []);

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className="bg-slate-900 py-16 px-4">
        <div className="max-w-4xl mx-auto">
          <Link to="/" className="inline-flex items-center gap-2 mb-8 text-orange-400 hover:text-orange-300 text-sm font-medium transition-colors">
            <span className="w-8 h-8 rounded-lg bg-orange-500 flex items-center justify-center text-white font-black text-sm">RS</span>
            ReStaff
          </Link>
          <h1 className="text-4xl sm:text-5xl font-bold text-white mb-4">ReStaff Blog</h1>
          <p className="text-slate-400 text-lg">Workforce management insights, scheduling strategies, and labor cost tips.</p>
        </div>
      </header>

      {/* Posts grid */}
      <main className="max-w-4xl mx-auto px-4 py-16">
        <div className="grid gap-10">
          {BLOG_POSTS.map((post) => (
            <article key={post.slug} className="group border border-slate-200 rounded-2xl overflow-hidden hover:shadow-lg transition-shadow duration-300">
              <Link to={`/blog/${post.slug}`}>
                <div className="relative overflow-hidden" style={{ height: '320px' }}>
                  <img
                    src={post.image}
                    alt={post.imageAlt}
                    loading="lazy"
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  />
                  <div className="absolute top-4 left-4">
                    <span className="bg-orange-500 text-white text-xs font-bold px-3 py-1 rounded-full uppercase tracking-wide">
                      {post.category}
                    </span>
                  </div>
                </div>
              </Link>
              <div className="p-8">
                <div className="flex items-center gap-4 text-sm text-slate-400 mb-4">
                  <span className="flex items-center gap-1.5"><User size={14} />{post.author}</span>
                  <span className="flex items-center gap-1.5"><Clock size={14} />{post.readTime}</span>
                </div>
                <Link to={`/blog/${post.slug}`}>
                  <h2 className="text-2xl font-bold text-slate-900 mb-3 hover:text-orange-500 transition-colors leading-snug">
                    {post.title}
                  </h2>
                </Link>
                <p className="text-slate-500 leading-relaxed mb-6">{post.excerpt}</p>
                <Link
                  to={`/blog/${post.slug}`}
                  className="inline-flex items-center gap-2 text-orange-500 font-semibold hover:gap-3 transition-all"
                >
                  Read Article <ArrowRight size={16} />
                </Link>
              </div>
            </article>
          ))}
        </div>
      </main>

      {/* Footer CTA */}
      <section className="bg-slate-900 py-16 px-4 text-center">
        <h2 className="text-3xl font-bold text-white mb-4">Ready to manage your workforce smarter?</h2>
        <p className="text-slate-400 mb-8 max-w-xl mx-auto">Start your 30-day free trial — no credit card required.</p>
        <Link
          to="/register"
          className="inline-flex items-center gap-2 bg-orange-500 hover:bg-orange-600 text-white font-bold px-8 py-4 rounded-xl transition-colors"
        >
          Start Free Trial <ArrowRight size={18} />
        </Link>
        <p className="text-slate-500 text-sm mt-8">
          Explore: <Link to="/employee-scheduling" className="text-orange-400 hover:underline">Employee Scheduling</Link> · <Link to="/time-tracking" className="text-orange-400 hover:underline">Time Tracking</Link> · <Link to="/timesheet-software" className="text-orange-400 hover:underline">Timesheets</Link> · <Link to="/labor-cost-management" className="text-orange-400 hover:underline">Labor Cost Management</Link> · <Link to="/task-management" className="text-orange-400 hover:underline">Task Management</Link>
        </p>
      </section>
    </div>
  );
}
